import type { TreatSlot } from "@highmarch/app";
import { checkHtml, wellHtml } from "../components";
import { icon } from "../iconsKit";
import { treatArtHtml } from "./treatArt";
import { pct, treatWellWords } from "./petWords";

/**
 * The battle card's Pet Treat well (Pets v2, docs/PETS.md), beside the pet chip: the armed biscuit (or a dim plain one)
 * with how many of it are held, "Pet Treat" and the coat chance a wild pet would roll with it ("Coat chance 3.5%").
 * A tap opens the little picker over it — the three biscuits with their counts and chances, a tap arms one (or puts the
 * armed one away) — or, holding none, goes to the Shop's Pet Treats (the battle card only). Strings and one popover;
 * features/pets.ts handles the taps (delegated) and the rules are @highmarch/app Pets.
 */

/** The well. `base`: the coat chance with no treat (%). */
export function treatWellHtml(slots: readonly TreatSlot[], base: number): string {
  const w = treatWellWords(slots, base), armed = slots.find((s) => s.armed) ?? null;
  const shown = armed ?? slots.find((s) => s.have > 0) ?? slots[0];
  const well = wellHtml({ art: treatArtHtml(shown?.treat.id ?? "biscuit", 34), size: "sm", count: armed ? armed.have : "", dim: armed === null, className: "ptwell" });
  const mark = armed ? checkHtml("supchk") : w.empty ? `<i class="pt-shop" aria-hidden="true">${icon("shop", 11)}</i>` : "";
  return `<button type="button" class="pettreat${armed ? " on" : ""}${w.empty ? " none" : ""}" aria-label="${w.title}: ${w.sub}. ${w.hint}">${well}${mark}`
    + `<span class="pt-t"><b class="disp">${w.title}</b><small>${w.sub}</small></span></button>`;
}

/** The picker's markup: one well per biscuit (dim when none is held), its chance, a check on the armed one; the Shop link. */
export function treatPickHtml(slots: readonly TreatSlot[], shopLink: boolean): string {
  const tiles = slots.map((s) => {
    const well = wellHtml({ art: treatArtHtml(s.treat.id, 40), size: "sm", count: s.have > 0 ? s.have : "", dim: s.have <= 0, className: "ptwell" });
    return `<button type="button" class="tp-one${s.armed ? " on" : ""}" data-treat="${s.treat.id}" aria-pressed="${s.armed}" ${s.have > 0 ? "" : "disabled"}>${well}${s.armed ? checkHtml("supchk") : ""}<small>${s.treat.name}</small><b>${pct(s.skinChancePercent)}</b></button>`;
  }).join("");
  const shop = shopLink ? `<button type="button" class="tp-shop" aria-label="Get Pet Treats in the Shop">${icon("shop", 16)}<small>Shop</small></button>` : "";
  return `<div class="treatpick" role="dialog" aria-label="Pick a Pet Treat"><p class="tp-h"><b class="disp">Pet Treats</b><small>Rare coat chance for the next wild pet</small></p><div class="tp-row">${tiles}${shop}</div></div>`;
}

/**
 * Opens the picker over `anchor` (the well), inside `root` (the hub or the map, so it goes with them); `pick(id)` on a biscuit tapped (its id, or null to put the
 * armed one away), `shop()` on the Shop link. A tap outside closes it. Returns its close.
 */
export function openTreatPick(root: HTMLElement, anchor: HTMLElement, slots: readonly TreatSlot[], shopLink: boolean, on: { pick(id: string | null): void; shop(): void }): () => void {
  document.querySelector(".treatpick")?.remove();
  const host = document.createElement("div");
  host.innerHTML = treatPickHtml(slots, shopLink);
  const el = host.firstElementChild as HTMLElement;
  root.append(el);
  const a = anchor.getBoundingClientRect(), r = root.getBoundingClientRect(), w = el.offsetWidth || 260;
  el.style.left = `${Math.max(8, Math.min(r.width - w - 8, a.right - r.left - w))}px`;
  el.style.top = `${Math.max(8, a.top - r.top - (el.offsetHeight || 120) - 8)}px`;
  const close = (): void => { el.remove(); document.removeEventListener("pointerdown", outside, true); };
  const outside = (e: Event): void => { if (!el.contains(e.target as Node) && !anchor.contains(e.target as Node)) close(); };
  document.addEventListener("pointerdown", outside, true);
  for (const b of el.querySelectorAll<HTMLButtonElement>(".tp-one")) b.onclick = () => { close(); on.pick(b.classList.contains("on") ? null : b.dataset.treat ?? null); };
  el.querySelector<HTMLButtonElement>(".tp-shop")?.addEventListener("click", () => { close(); on.shop(); });
  return close;
}
